import { useState } from 'react'
import { exportGenerationReportBlob, exportGenerationsToExcel, type ProductAnalyticsState } from '../../model/analytics'
import { useProductFlowForm } from '../../model/productFlow'
import { Card } from '@/common/ui/Card'
import { downloadBlobAsFile, getDownloadFileExtensionFromMime } from '@/common/ui/DownloadBlobAsFile'
import { Button } from '@/common/ui/Form/Button'
import { TokenAmount } from '@/common/ui/TokenAmount'
import { Icon } from '@/common/ui/Icon'

const buildReportDownloadFilename = (blob: Blob, generationId: string) => {
    const date = new Date().toISOString().split('T')[0]
    const ext = getDownloadFileExtensionFromMime(blob.type)
    return ext ? `report_${generationId}_${date}.${ext}` : `report_${generationId}_${date}`
}

const getAnnotationScore = (common_score: number, max_score: number = 10) => {
    if (common_score === null || common_score === undefined) {
        return null
    }
    if (common_score <= 2) {
        return (
            <>
                Это низкий результат,
                <br />
                нужно улучшить
            </>
        )
    }
    if (common_score <= 4 * (max_score || 10)) {
        return (
            <>
                Это хороший результат,
                <br />
                но ещё есть над чем работать
            </>
        )
    }

    if (common_score <= 7 * (max_score || 10)) {
        return (
            <>
                Это хороший результат,
                <br />
                но ещё есть над чем работать
            </>
        )
    }
    return <>Это отличный результат!</>
}

type AnalyticsFormProps = {
    analytics: ProductAnalyticsState
}

export const AnalyticsForm = ({ analytics }: AnalyticsFormProps) => {
    const {
        isProductLoading,
        submitGeneration,
        generationMutation,
        orgId,
        productId,
        product,
        generationId,
        validationResult,
        showAnalysisStartCard,
        isSubmitting,
    } = analytics

    const [isExportingExcel, setIsExportingExcel] = useState(false)
    const [isDownloadingReport, setIsDownloadingReport] = useState(false)
    const canSubmit = Boolean(orgId && productId && product)

    const { handleSubmit, formState, clearErrors } = useProductFlowForm<Record<string, never>>({
        defaultValues: {},
        generationMutation,
    })

    const handleDownloadReport = async () => {
        if (!generationId || !orgId) return

        try {
            setIsDownloadingReport(true)
            const blob = await exportGenerationReportBlob(orgId, generationId, false)
            downloadBlobAsFile(blob, buildReportDownloadFilename(blob, generationId))
        } catch (error) {
            console.error('Ошибка при скачивании отчёта:', error)
        } finally {
            setIsDownloadingReport(false)
        }
    }

    const handleExportExcel = async () => {
        if (!generationId || !orgId) return

        try {
            setIsExportingExcel(true)
            const blob = await exportGenerationsToExcel(orgId, [generationId])
            downloadBlobAsFile(blob, `report_${generationId}_${new Date().toISOString().split('T')[0]}.xlsx`)
        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error)
        } finally {
            setIsExportingExcel(false)
        }
    }

    return (
        <>
            {isProductLoading && (
                <div className='flex justify-center items-center h-64' role='status' aria-live='polite'>
                    Загрузка...
                </div>
            )}
            {showAnalysisStartCard && (
                <Card type='result-last' className='overflow-hidden w-full'>
                    <form
                        onSubmit={handleSubmit(() => {
                            clearErrors()
                            submitGeneration()
                        })}
                    >
                        <div className='flex flex-col items-center gap-4 w-full'>
                            <div className='flex flex-col items-center gap-[7px] text-center pb-1'>
                                <h2 className='text-[30px] font-medium text-white mt-[-12px]'>
                                    Анализ карточки товара
                                </h2>
                                <p className='text-base text-[#aba7af] max-w-[400px]'>
                                    Рекомендации по улучшению, генерация текстов и медиа-файлов с помощью нейросетей
                                </p>
                            </div>
                            {formState.errors.root?.message && (
                                <p className='text-sm text-red-400 text-center max-w-[400px]' role='alert'>
                                    {formState.errors.root.message}
                                </p>
                            )}
                            <Button
                                type='submit'
                                variant={isSubmitting ? 'outline' : 'filled'}
                                disabled={isSubmitting || !canSubmit}
                                loading={isSubmitting}
                                size='lg'
                                aria-label='Запустить анализ карточки товара'
                            >
                                <span>{isSubmitting ? 'Выполняется' : 'Запустить'}</span>
                                {!isSubmitting && <TokenAmount value={24} />}
                            </Button>
                        </div>
                    </form>
                </Card>
            )}
            {validationResult && !showAnalysisStartCard && (
                <Card type='result-first' className='w-full'>
                    <div className='flex flex-col gap-[12px] items-start'>
                        <h2 className='font-medium text-3xl text-white leading-[1.4]'>Отчёт</h2>
                        <div className='text-[50px] font-bold leading-[1.4] bg-gradient-to-r from-[#C0ACE0] to-[#C535F1] bg-clip-text text-transparent'>
                            {validationResult.common_card_score}&#8202;/&#8202;{validationResult.max_score}
                        </div>

                        <div className='result-blocks-container'>
                            <div className='flex-1 text-[#aba7af] text-base leading-[1.4] min-w-0'>
                                {getAnnotationScore(validationResult.common_card_score, validationResult.max_score)}
                            </div>

                            <div className='flex gap-3 min-w-0 flex-shrink-0'>
                                <form
                                    onSubmit={handleSubmit(() => {
                                        clearErrors()
                                        submitGeneration()
                                    })}
                                >
                                    <Button type='submit' size='md' variant='ghost' title='Перезапустить анализ'>
                                        <Icon name='refresh' size='md' />
                                    </Button>
                                </form>

                                <Button
                                    className='bg-gradient-to-b from-[#be96ff] to-[#7641e2] flex gap-1.5 items-center justify-center px-4 py-2.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
                                    type='button'
                                    size='md'
                                    onClick={handleDownloadReport}
                                    disabled={isDownloadingReport}
                                    loading={isDownloadingReport}
                                    title='Скачать отчёт'
                                    aria-label='Скачать отчёт'
                                >
                                    {!isDownloadingReport && (
                                        <>
                                            <Icon name='export' className='w-[1.125rem] h-[1.125rem]' />
                                            <span className='text-white text-base'>Скачать</span>
                                        </>
                                    )}
                                </Button>
                                <Button
                                    onClick={handleExportExcel}
                                    disabled={isExportingExcel}
                                    loading={isExportingExcel}
                                    className='px-1'
                                    type='button'
                                    size='md'
                                    title='Экспорт в Excel'
                                >
                                    {!isExportingExcel && <Icon name='xls' className='w-[1.125rem] h-[1.125rem]' />}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </>
    )
}
